import { CommonEntitiesLocale, CommonLocale, ContentExposureLocale } from "@/locales/localeid";
import { fmtMsg } from "./../../../commonui/utils/func";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Icon, Drawer, Button, Tree, Tooltip, Empty, Modal } from "ant-design-vue";
import { ObjectiveService, ITreeNode } from "@/services/objective-manager";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { pureRichText } from "@/util/func";
import { sortBy } from "lodash";
import { TeachingActivityModel } from "@/services/teaching-activity/model";

export interface ContentExposureObjectiveProps {
	isEdit?: boolean;
	isAnyEdit?: boolean;
	initValues?: any[];
}

@Component({
	components: {
		Icon,
		Tree,
		Drawer,
		Button,
		Tooltip,
		Empty
	}
})
export default class ContentExposureObjective extends Vue {
	public titleListModule = getModule(TitleListState);
	@Prop() versionGroupId!: string;
	@Prop() vcsLevelId!: string;
	@Prop() contentId!: string;
	@Prop({ default: false }) isEdit!: boolean;
	@Prop({ default: false }) isAnyEdit!: boolean;
	@Prop({ default: () => [] }) initValues!: any[];
	@Prop({ default: false }) showTooltip!: boolean; // to all disable tooltip permanently
	@Prop({ default: () => [] }) teachingActivities!: TeachingActivityModel[];
	public objectivesTree: ITreeNode[] = [];
	public showLine = true;
	public showIcon = true;
	public autoExpandParent = true;
	public cacheNodeId: string = "";
	public checkedKeys: string[] = [];
	public expandedKeys: string[] = [];
	public ObjectiveNodes: ITreeNode[] = [];

	visible = false;
	tooltipVisible = false;
	locale = CommonLocale;

	$refs!: {
		content: HTMLDivElement;
	};

	get drawerTitle() {
		return fmtMsg(CommonEntitiesLocale.CommonObjectivesText);
	}

	get objectives() {
		this.initValues.map((v) => {
			v.title = pureRichText(v.text);
			return v;
		});

		return sortBy(this.initValues, "sequence");
	}

	mounted() {
		const parent = this.$refs.content.parentNode as HTMLTableCellElement;
		if (parent) {
			parent.addEventListener("dblclick", this.onDoubleClick);
			parent.addEventListener("mouseenter", this.onMouseEnter);
			parent.addEventListener("mouseleave", this.onMouseLeave);
		}
	}

	beforeDestroy() {
		const parent = this.$refs.content.parentNode as HTMLTableCellElement;
		if (parent) {
			parent.removeEventListener("dblclick", this.onDoubleClick);
			parent.removeEventListener("mouseenter", this.onMouseEnter);
			parent.removeEventListener("mouseleave", this.onMouseLeave);
		}
	}

	onDoubleClick() {
		!this.isEdit && this.$emit("doubleClick");
	}

	onMouseEnter() {
		this.tooltipVisible = true;
	}

	onMouseLeave() {
		this.tooltipVisible = false;
	}

	setObjectives(objectives) {
		this.objectivesTree = this.formatTreeLevel(objectives);
	}

	async getObjectivesTree() {
		const { vcsLevelId } = this.titleListModule.editedContentDetails;
		const { editedContentId } = this.titleListModule;
		const params = {
			level: 3,
			mustDeepest: true,
			startUnitId: this.vcsLevelId || vcsLevelId,
			endUnitId: this.vcsLevelId || vcsLevelId,
			versionGroupId: this.versionGroupId,
			contentId: this.contentId || editedContentId
		};
		const response = await ObjectiveService.getObjectivesTree(params);
		this.setObjectives(response.children);
	}

	@Watch("visible")
	getTeachingActivities() {
		if (!this.visible) return;
		this.getObjectivesTree();
	}

	onExpand(expandedKeys: []): void {
		this.expandedKeys = expandedKeys;
		this.autoExpandParent = false;
	}

	onCheck(keys) {
		this.checkedKeys = keys;
	}

	onSelectNode(event: any): void {
		const id = !event.length ? this.cacheNodeId : event[0];
		this.cacheNodeId = id;
		const index = this.checkedKeys.findIndex((key) => key == id);
		if (index > -1) {
			this.checkedKeys.splice(index, 1);
		} else {
			this.checkedKeys.push(id);
		}
	}

	showDrawer() {
		this.visible = true;
	}
	onClose() {
		this.visible = false;
		this.checkedKeys = [];
		this.ObjectiveNodes = [];
	}
	onSubmit() {
		const selectedObjectives = this.checkedKeys.map((checkedKey) => {
			const node = this.ObjectiveNodes.find((o) => o.id == checkedKey);
			return {
				text: node?.title,
				sequence: node?.sequence,
				objectiveId: checkedKey,
				contentId: this.titleListModule.editedContentId,
				contentObjective: node && node.contentObjective.slice(0, 1).shift()
			};
		});

		const associatedObjectiveIds = this.teachingActivities
			.filter((ta) => ta.objectiveId !== null)
			.map((ta) => ta.objectiveId);
		const selectedObjectiveIds = selectedObjectives.map((obj) => obj.objectiveId);
		// show warning message if any of the objectives present in teachingactivites are not present in selectedObjectives
		if (associatedObjectiveIds.some((ta) => ta?.toString() && !selectedObjectiveIds.includes(ta as string))) {
			Modal.confirm({
				title: fmtMsg(ContentExposureLocale.ContentExposureObjectiveRemoveObjectiveWarningTitle),
				content: fmtMsg(ContentExposureLocale.ContentExposureObjectiveRemoveObjectiveWarningDescription),
				okText: "Yes",
				okType: "danger",
				cancelText: "No",
				onOk: () => {
					this.onClose();
					this.$emit("saveAssociatedTAObjectives", {
						selectedObjectiveIds,
						teachingActivities: this.teachingActivities
					});
					this.$emit("submit", sortBy(selectedObjectives, "sequence"));
				},
				onCancel: () => {
					const objectiveIdsToCheck = associatedObjectiveIds.filter(
						(ta) => !selectedObjectiveIds.includes(ta as string)
					);
					this.checkedKeys.push(...(objectiveIdsToCheck as string[]));
					const newSelectedObjectives = (objectiveIdsToCheck as string[]).map((checkedKey) => {
						const node = this.ObjectiveNodes.find((o) => o.id == checkedKey);
						return {
							text: node?.title,
							sequence: node?.sequence,
							objectiveId: checkedKey,
							contentId: this.titleListModule.editedContentId,
							contentObjective: node && node.contentObjective.slice(0, 1).shift()
						};
					});
					selectedObjectives.push(...newSelectedObjectives);
					this.$emit("submit", sortBy(selectedObjectives, "sequence"));
				}
			});
		} else {
			this.onClose();
			this.$emit("submit", sortBy(selectedObjectives, "sequence"));
		}
	}

	formatTreeLevel(array) {
		let sequence = 0;
		return array.map((p) => {
			Object.assign(p, { key: p.id, title: p.text, checkable: false, scopedSlots: { title: "custom" } });
			if (p.children.length) {
				this.expandedKeys.push(p.key);
				p.children.forEach((child) => {
					this.ObjectiveNodes = this.ObjectiveNodes.concat(child);
					if (this.objectives.some((v) => v.objectiveId == child.id)) {
						this.checkedKeys.push(child.id);
					}
					Object.assign(child, {
						key: child.id,
						title: child.text,
						text: pureRichText(child.text),
						checkable: true,
						sequence: sequence++,
						scopedSlots: { title: "custom" }
					});
				});
			}
			return p;
		});
	}
}
